/**
 * One strip across the top of the Lab board: the piece, the bars, the players, and go.
 *
 * These four things were a column of cards, and a column of cards is a form -- something to
 * be filled in once, top to bottom, before anything happens. It is the wrong shape for what
 * this board is actually used for, which is asking the same question again with one thing
 * changed: another four bars, one more viola, the same piece for winds. A toolbar keeps all
 * four visible and one click from each other while the score sits underneath, which is how
 * every editor arranges a document and its settings.
 *
 * The file chosen is handed to the board, which reads it (`lib/arrange-open.ts`): MusicXML and
 * MIDI in the browser, at once, and a PDF of printed music on the server.
 *
 * What a roster silences is said here rather than in the result. A roster that mixes groups,
 * or that seats fewer players than the balance rule will weigh, takes that rule out of reach
 * before the run; saying so afterwards is telling the reader what they can no longer change.
 * See `lib/roster-notice.ts`, which keeps the generator's own words. The roster itself --
 * presets, one more or one fewer of each player, clearing it -- is `ArrangeRoster`.
 *
 * Once a run has come back the strip folds to one line -- the piece, the bars, the players,
 * a way back in, and Arrange again -- because by then the settings are known and the desks
 * below are what is being looked at; a strip wrapped to three rows on a narrow window was a
 * third of the height the score needed. Unfolding it is one click, and opening another file
 * or clearing the result unfolds it by itself (the board owns `folded`).
 *
 * Once a run has come back, Another take arranges the same again as the next take (2, 3 and
 * on): where two chords or two placements of a chord are as good, the arranger chooses another,
 * and the same take of the same settings is always the same. Arrange always runs take 1, the
 * one that makes every choice the ranking puts first.
 *
 * Where arranging starts (`ArrangeStartMenu`) comes before the choices, since starting from the
 * tune shuts them; the AI arranger and the check (`ArrangeReview`) follow the choices. Starting
 * from the tune has one take, is said under the strip while it is on, and refuses a roster with
 * a player of two hands before the run. Starting from the note model shuts the choices too, is said
 * the same way, refuses a roster the model does not write for (`modelWrites`), and always has
 * another take: each is another writing by the model. The folded line names all three.
 *
 * The sentences these controls used to carry underneath them are now the titles on the
 * controls themselves. Nothing was dropped: what the bar window (`ArrangeBars`) does with an
 * end past the end of the piece, and what becomes of a chord with more notes in it than there are
 * players, are both still said -- the second by the reading bar, which reports it as a
 * number once there is a number to report.
 */

import { useRef } from "react";
import type { Seat } from "@app/shared-types";
import ArrangeBars from "./ArrangeBars";
import ArrangeChoices, { type Choices } from "./ArrangeChoices";
import ArrangeReview, { type Review, reviewed } from "./ArrangeReview";
import ArrangeRoster, { presetOf, silencedBy } from "./ArrangeRoster";
import ArrangeStartMenu, { anewOf, SAID, SHUT, unseatable } from "./ArrangeStartMenu";
import { ACTION, Divider, Run, STRIP } from "./Chrome";
import { SCORE_ACCEPT } from "../lib/arrange-open";
import type { Picked } from "../lib/arrange-pick";
import { bytes } from "../lib/format";

export type { Picked };

export interface ArrangeToolbarProps {
  readonly picked: Picked | null;
  /** Whether the run takes every bar rather than the window typed. */
  readonly whole: boolean;
  readonly first: string;
  readonly last: string;
  /** Bars in the opened piece, as this browser read it; `null` where it could not. */
  readonly total: number | null;
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
  readonly running: boolean;
  readonly choices: Choices;
  /** The AI arranger's level and the check; apart from the rules arranger's `choices`. */
  readonly review: Review;
  /** Shown as one line of what was asked, the controls behind a button. */
  readonly folded: boolean;
  /** Whether there is a run to fold the strip over, and so a button to fold it. */
  readonly foldable: boolean;
  onFold(folded: boolean): void;
  /** A score picked: one file, or several photos or pictures of its pages. */
  onOpen(files: readonly File[]): void;
  onWhole(whole: boolean): void;
  onWindow(first: string, last: string): void;
  onRoster(roster: string[]): void;
  onChoices(choices: Choices): void;
  onReview(review: Review): void;
  onRun(): void;
  /** Which take the run shown is, from 1; see `onAnother`. */
  readonly take: number;
  /** Arrange the same again as the next take: see the module. */
  onAnother(): void;
}

export default function ArrangeToolbar({
  picked,
  whole,
  first,
  last,
  total,
  roster,
  seats,
  running,
  choices,
  review,
  folded,
  foldable,
  onFold,
  onOpen,
  onWhole,
  onWindow,
  onRoster,
  onChoices,
  onReview,
  onRun,
  take,
  onAnother,
}: ArrangeToolbarProps): JSX.Element {
  const picker = useRef<HTMLInputElement>(null);

  const silenced = silencedBy(roster, seats);
  const anew = anewOf(review.start);
  const tune = anew === "tune";
  const refused = unseatable(roster, review.start);
  const run = (
    <Run
      label={running ? "Arranging…" : folded ? "Arrange again" : "Arrange"}
      disabled={running || picked === null || roster.length === 0 || refused !== null}
      title={
        picked === null
          ? "Open a score first: MusicXML, MIDI, or a PDF or photos of printed music."
          : roster.length === 0
            ? "Choose who is playing first: no roster is assumed for you."
            : (refused ?? undefined)
      }
      onRun={onRun}
    />
  );

  // Only the chords, the notes a harmony level adds, a counter-melody and the neighbour notes
  // and turns varying the tune are chosen between; the source's own notes are handed out the
  // same way every time, and a light variation's passing notes are the only ones there are, so
  // As written with neither of the others has one take.
  const single =
    anew === null &&
    choices.harmony === "written" &&
    choices.counter === "off" &&
    (choices.vary === "off" || choices.vary === "light");
  const another = foldable ? (
    <>
      {take > 1 ? <span className="font-mono text-[10px] text-shell-dim">take {take}</span> : null}
      <button
        type="button"
        onClick={onAnother}
        disabled={running || single || tune}
        title={
          tune
            ? "Starting from the tune writes one arrangement of the same settings: it has no other take."
            : single
              ? "As written adds nothing to choose between, so every take of it is the same. Choose a harmony level, a counter-melody or a tune varied More or Free to hear another."
              : anew === "model"
                ? `Have the note model write it again as take ${take + 1}: another writing of the parts under the same tune. The same take is always the same; Arrange goes back to take 1.`
                : `Arrange it again as take ${take + 1}: where two chords, two placements of a chord, two notes of a counter-melody or two ornaments of the tune are as good, another one is chosen, never a chord or placement that rubs more against the tune. The same take of the same settings is always the same; Arrange goes back to take 1.`
        }
        className={`${ACTION} disabled:text-shell-dim disabled:hover:bg-transparent`}
      >
        Another take
      </button>
    </>
  ) : null;

  if (folded) {
    const players = presetOf(roster) || `${roster.length} player${roster.length === 1 ? "" : "s"}`;
    return (
      <div className={STRIP}>
        <p className="min-w-0 flex-1 truncate text-[11px] text-shell-text">
          <span className="text-shell-lit">{picked?.name ?? "No score"}</span>
          {" · "}
          {whole ? "whole piece" : `bars ${first || "1"}–${last || "end"}`}
          {" · "}
          {players}
          {reviewed(review)}
        </p>
        <button type="button" onClick={() => onFold(false)} className={ACTION}>
          Settings
        </button>
        {another}
        {run}
      </div>
    );
  }

  return (
    <div className={STRIP}>
      <button
        type="button"
        onClick={() => picker.current?.click()}
        className="flex h-7 max-w-[240px] items-center gap-1.5 rounded bg-shell-key px-2.5 text-[11px] text-shell-lit hover:bg-shell-hot"
      >
        <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
          <path
            d="M2 4.5A1.5 1.5 0 0 1 3.5 3h3l1.5 2h4.5A1.5 1.5 0 0 1 14 6.5v5A1.5 1.5 0 0 1 12.5 13h-9A1.5 1.5 0 0 1 2 11.5z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
        </svg>
        <span className="truncate">{picked === null ? "Open a score…" : picked.name}</span>
      </button>
      {picked === null ? null : (
        <span className="font-mono text-[10px] text-shell-dim">
          {bytes(new TextEncoder().encode(picked.text).length)}
        </span>
      )}
      <input
        ref={picker}
        type="file"
        accept={SCORE_ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length > 0) onOpen(files);
        }}
      />

      <Divider />

      <ArrangeBars
        whole={whole}
        first={first}
        last={last}
        total={total}
        onWhole={onWhole}
        onWindow={onWindow}
      />

      <Divider />

      <ArrangeRoster roster={roster} seats={seats} onRoster={onRoster} />

      <Divider />

      <ArrangeStartMenu value={review.start} onPick={(start) => onReview({ ...review, start })} />

      <ArrangeChoices
        choices={choices}
        shut={anew === null ? undefined : SHUT[anew]}
        onChoices={onChoices}
      />

      <ArrangeReview review={review} roster={roster} seats={seats} onReview={onReview} />

      {silenced.length === 0 && anew === null ? null : (
        <p
          role="status"
          className="order-last w-full rounded border-l-2 border-unread bg-shell-key/60 px-2 py-1.5 text-[11px] leading-relaxed text-shell-text"
        >
          {[
            ...(anew === null ? [] : [SAID[anew], ...(refused === null ? [] : [refused])]),
            ...silenced,
          ].join(" ")}
        </p>
      )}

      <div className="ml-auto flex items-center gap-2">
        {foldable ? (
          <button type="button" onClick={() => onFold(true)} className={ACTION}>
            Hide settings
          </button>
        ) : null}
        {another}
        {run}
      </div>
    </div>
  );
}
